import React, { Component, PropTypes } from 'react';
import './CreateRoomForm.css';

import UserAvatar from './UserAvatar.js';

class CreateRoomForm extends Component {
  constructor(props) {
    super(props);

    // Flag for whether or not the drop zone is being dragged over by a user
    // right now.
    this.state = {
      isBeingDraggedOver: false,
      hasChangedRoomName: false,

      roomName: '',
      buildName: '',
      recipients: []
    };
  }

  /**
   * Handle onDragOver events for the drop zone.
   *
   * @memberof CreateRoomForm
   * @param SyntheticEvent The drag event.
   */
  handleDragOver = (e) => {
    this.setState({ isBeingDraggedOver: true });
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    return false;
  }

  /**
   * Handle onDragEnter events for the drop zone.
   *
   * @memberof CreateRoomForm
   * @param SyntheticEvent The drag event.
   */
  handleDragEnter = (e) => {
    this.setState({ isBeingDraggedOver: true });
  }

  /**
   * Handle onDragLeave events for the drop zone.
   *
   * @memberof CreateRoomForm
   * @param SyntheticEvent The drag event.
   */
  handleDragLeave = (e) => {
    this.setState({ isBeingDraggedOver: false });
  }

  handleDrop = (ev) => {
    // Attempt to fetch the user attached to the dropped object. If one doesn't exist,
    // don't try to accept it and return early.
    let data = ev.dataTransfer.getData('user');
    if (!data) {
      return;
    }

    // Otherwise, accept the drop.
    ev.stopPropagation();
    this.setState({ isBeingDraggedOver: false });

    // Handle dropping the user...
    let user = JSON.parse(data);
    this.addFriend(user);

    return false;
  }


  addFriend = (user) => {
    let { recipients, hasChangedRoomName, roomName } = this.state;
    recipients.push(user);

    let stateChanges = { recipients };

    // If the user hasn't supplied a custom room name yet, populate it
    // with the recipients.
    if (!hasChangedRoomName || !roomName) {
      let displayNames = recipients.map((user) => user.displayName);
      stateChanges.roomName = `Chat with ${displayNames.join(', ')}`;
      stateChanges.hasChangedRoomName = false;
    }

    this.setState(stateChanges);
  }

  handleRoomNameInput = (ev) => {
    this.setState({ hasChangedRoomName: true, roomName: ev.target.value });
  }

  handleBuildNameInput = (ev) => {
    this.setState({ buildName: ev.target.value });
  }

  /**
   * Prevent the default form submit behaviour, and then send the onSubmit action
   * up to the WelcomePage to trigger the Redux action and appropriate side effects.
   *
   * @memberof CreateRoomForm
   * @param SyntheticEvent The submit event from the React form DOM element.
   */
  createRoom = (ev) => {
    ev.preventDefault();

    const { recipients, buildName, roomName } = this.state;
    const { onSubmit } = this.props;

    let members = recipients.map((user) => user.id);

    let room = {
      name: roomName,
      build: buildName,
      members
    };

    onSubmit(room);
  }

  /**
   * Render out a friend cell for a given user object. Renders the circular cells that denote
   * who will be invited when the room is created.
   *
   * @memberof CreateRoomForm
   * @param Object The user object from the API to render.
   */
  renderFriendCell = (user) => {
    return (
      <li className="CreateRoomForm__user-cell">
        <UserAvatar user={user} />
        <span className="CreateRoomForm__user-cell-name">{user.displayName}</span>
      </li>
    );
  }

  /**
   * Render out the form. Either renders the success message (if this.props.createdRoom is
   * truthy) or the room creation form.
   *
   * @returns
   * @memberof CreateRoomForm
   */
  render() {
    const { isBeingDraggedOver, recipients, roomName, buildName } = this.state;
    const { currentUser, createdRoom, onFinish } = this.props;

    let invited = recipients.map((user) => user.displayName);

    // prefix the last user invited with `and` (this is horrible for i18n, please FIXME!)
    if (invited.length > 2) {
      let lastItem = invited.length - 1;
      invited[lastItem] = `and ${invited[lastItem]}`;
    }

    // If there's been a room created, return early with the success message.
    let createdMessage;
    if (createdRoom) {
      createdMessage = <p>We've gone ahead and created the room {createdRoom.name}.</p>;
      if (invited.length) {
        createdMessage = <p>We've gone ahead and created the room {createdRoom.name} and invited {invited.join(', ')}.</p>;
      }

      return (
        <div className="CreateRoomForm">
          <h3>Room created!</h3>
          <p>{createdMessage}</p>
          <button className="Button" onClick={() => onFinish(true)}>Join room and close</button>
          <button className="Button" onClick={() => onFinish(false)}>Close</button>
        </div>
      )
    }

    // Otherwise, assemble the room creation form.

    // Compute the help text for the drop zone (changes depending on if there's any items
    // being dragged over the form)..
    let dropzoneClasses = ['CreateRoomForm__dropzone'];
    let dropzoneText = 'To invite people to the room, drag them from your friends list on the left.';
    if (isBeingDraggedOver) {
      dropzoneClasses.push('is-drag-over');
      dropzoneText = 'Now drop to add the user!';
    }

    // Map through each invited user and render a cell for them.
    let recipientCells = recipients.map((user) => this.renderFriendCell(user));

    // If the current user is an admin, allow them to supply a custom build name.
    let buildNameField;
    if (currentUser.admin) {
      buildNameField = <input type="text" className="CreateRoomForm__name-input" placeholder="Enter a build name..." value={buildName} onChange={this.handleBuildNameInput} />;
    }

    // Then, assemble the final DOM form.
    return (
      <form action="#" className="CreateRoomForm" onSubmit={this.createRoom}>
        <input type="text" className="CreateRoomForm__name-input" placeholder="Enter a room name..." value={roomName} onChange={this.handleRoomNameInput} />

        {buildNameField}

        <ul className="CreateRoomForm__user-list">
          {recipientCells}
        </ul>

        <section className={dropzoneClasses.join(' ')}
          onDragOver={this.handleDragOver}
          onDragEnter={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}>
          <p className="CreateRoomForm__drop-help">{dropzoneText}</p>
        </section>

        <input type="submit" className="Button CreateRoomForm__submit" value="Invite" />
      </form>
    );
  }
}

CreateRoomForm.propTypes = {
  createdRoom: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired
};

export default CreateRoomForm;



// WEBPACK FOOTER //
// ./src/components/CreateRoomForm.js